/// <amd-module name="Build/Pipelines/Components/PipelineTriggerIssuesPage" />
import * as React from "react";
import * as Resources from "Build/Pipelines/Resources";
import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Page } from "VSSUI/Page";
import { Table, ITableColumn, TwoLineTableCell, SimpleTableCell } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import * as PipelineTriggerIssuesStore from "Stores/PipelineTriggerIssuesStore";
import { Tooltip } from "VSSUI/TooltipEx";
import { Status, StatusSize } from "VSSUI/Status";
import { Ago } from "VSSUI/Ago";
import * as Utils_Date from "VSSUI/Utilities/Date";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IBuildLinkingService } from "Build/Client/Linking";
import { ObservableValue } from "VSS/Core/Observable";

interface ITriggerIssuesProps {
    definitionId: number;
    onBackButton: () => void;
    triggerIssuesData: PipelineTriggerIssuesStore.ITriggerIssuesGridRow[];
}

export class PipelineTriggerIssuesPage extends VssComponent<ITriggerIssuesProps> {
    constructor(props: ITriggerIssuesProps, context: IVssComponentContext) {
        super(props, context);
    }

    public render() {
        return (
            <Page className="flex-grow">
                <div className="pipeline-trigger-issues page-content page-content-top page-content-left">
                    <Header
                        title={Resources.TriggerIssues}
                        titleClassName="flex-row flex-center"
                        titleSize={TitleSize.Large}
                        backButtonProps={{ onClick: this.props.onBackButton }} />
                    <Card
                        className="flex-grow bolt-table-card margin-top-16 margin-left-8 bolt-card-no-vertical-padding"
                        contentProps={{ contentPadding: false }}>
                        {
                            this.props.triggerIssuesData.length > 0 ? this._getTriggerIssuesView() : this._getNoTriggerIssuesView()
                        }
                    </Card>
                </div>
            </Page>
        );
    }

    private _getTriggerIssuesView(): React.ReactNode {
        let itemsProvider = new ArrayItemProvider<PipelineTriggerIssuesStore.ITriggerIssuesGridRow>(this.props.triggerIssuesData);
        return (
            <Table
                ariaLabel={Resources.TriggerIssues}
                className="trigger-issues-table"
                columns={this._tableColumns}
                itemProvider={itemsProvider}
                scrollable={true}
            />
        );
    }

    private _getNoTriggerIssuesView(): React.ReactNode {
        return (
            <div className="no-trigger-issues">
                <Icon iconName={"Info"} className={"no-restrictions-icon flex-self-center"} ariaLabel="no-restrictions-icon" />
                <div className="message-container">
                    <span className="message-header title-s">{Resources.NoTriggerIssuesFound}</span>
                </div>
            </div>
        );
    }

    private _renderIssuesCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineTriggerIssuesStore.ITriggerIssuesGridRow>, tableItem: PipelineTriggerIssuesStore.ITriggerIssuesGridRow): JSX.Element => {
        let errormsgClassName = "";
        if (!tableItem.alias && !tableItem.source) {
            errormsgClassName = "trigger-issue-error-message";
        }
        var buildURL: string[] = tableItem.source.split(" ");
        var projectId = buildURL[1];
        var buildId = Number(buildURL[2]);
        let linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        let runPageUrl: string = linkingService.getBuildUrl(buildId, "", projectId);
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                key={"col-" + columnIndex}
                tableColumn={tableColumn}
                iconProps={{
                    render: () => {
                        return (<Status
                            {...tableItem.statusType}
                            key={tableItem.statusType + ""}
                            size={StatusSize.l}
                            className="flex-self-center margin-right-8"
                        />);
                    }
                }}
                className={errormsgClassName}
                line1={
                    <span className="flex-row scroll-hidden flex-center">
                        <Tooltip overflowOnly={true}>
                            <span className="fontWeightSemiBold font-weight-semibold fontSizeM font-size-m text-ellipsis">{tableItem.errorMessage}</span>
                        </Tooltip>
                    </span>
                }
                line2={
                    !tableItem.alias && !tableItem.source ? null :
                        <span className="fontSize font-size secondary-text flex-row scroll-hidden flex-center">
                            <Tooltip overflowOnly={true}>
                                <div>
                                    {!tableItem.source && <span className="padding-left-4 text-ellipsis">{tableItem.alias}</span>}
                                    {tableItem.source && buildURL && buildURL[0] &&
                                        <FPSLink className="icon-margin bolt-table-link text-ellipsis" href={runPageUrl}>
                                            {tableItem.alias + "/#" + buildURL[0]}
                                        </FPSLink>
                                    }
                                </div>
                            </Tooltip>
                        </span>
                }
            />
        );
    }

    private _renderYAMLCommitCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineTriggerIssuesStore.ITriggerIssuesGridRow>, tableItem: PipelineTriggerIssuesStore.ITriggerIssuesGridRow): JSX.Element => {
        let yamlCommit = tableItem.yamlCommit.substring(0, 8);
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true}>
                        <FPSLink className="icon-margin bolt-table-link text-ellipsis" href={tableItem.repositoryUrl.concat("/commit/" + tableItem.yamlCommit + "/")}>
                            {yamlCommit}
                        </FPSLink>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private _renderDateCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineTriggerIssuesStore.ITriggerIssuesGridRow>, tableItem: PipelineTriggerIssuesStore.ITriggerIssuesGridRow): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true}>
                        <Ago date={tableItem.date} format={Utils_Date.AgoFormat.Compact} />
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private _tableColumns: ITableColumn<PipelineTriggerIssuesStore.ITriggerIssuesGridRow>[] = [
        {
            id: "issues",
            width: new ObservableValue(-75),
            name: Resources.Issues,
            renderCell: this._renderIssuesCell.bind(this),
            ariaLabel: Resources.TriggerIssuesColumnName
        },
        {
            id: "yaml-commit",
            width: new ObservableValue(-25),
            name: Resources.YAMLCommit,
            renderCell: this._renderYAMLCommitCell.bind(this),
            ariaLabel: Resources.YAMLCommit
        }
    ];
}